// # Notification Session Manager
import { doc, getDoc, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

class NotificationSessionManager {
  constructor() {
    // # Initialize notification state
    this._notifications = null;
    this._adminNotifications = {
      subscriptions: {},
      subscription_forms: {},
      feedbacks: {},
      reviews: {}
    };
    this._subsc_ending = null;
    this._notificationListeners = new Map();
    this._meetings = {}; // # Changed from array to object (map)
  }

  // # Initialize notifications
  async initializeNotifications(uid) {
    try {
      // # Get user notifications
      const userNotifyRef = doc(db, 'user_notify', uid);
      const userNotifyDoc = await getDoc(userNotifyRef);
      
      // # Initialize user notifications
      if (userNotifyDoc.exists()) {
        const data = userNotifyDoc.data();
        this._notifications = data.subscriptions || {};
        this._subsc_ending = data.subsc_ending || {};
        // # Add meeting notifications if they exist (handle both map and array format for backward compatibility)
        if (data.meetings) {
          if (Array.isArray(data.meetings)) {
            // # Convert old array format to map format
            this._meetings = data.meetings.reduce((acc, meeting) => {
              if (meeting && meeting.id) {
                acc[meeting.id] = meeting;
              }
              return acc;
            }, {});
          } else {
            // # Use map format directly
            this._meetings = data.meetings || {};
          }
        } else {
          this._meetings = {};
        }
      } else {
        this._notifications = {};
        this._subsc_ending = {};
        this._meetings = {};
      }

      // # Only try to get admin notifications if user has admin claim
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdTokenResult();
        if (token.claims?.admin) {
          // # Get admin notifications if user is admin
          const adminNotifyRef = doc(db, 'adm_notify', uid);
          const adminNotifyDoc = await getDoc(adminNotifyRef);
          
          if (adminNotifyDoc.exists()) {
            const adminData = adminNotifyDoc.data();
            this._adminNotifications = {
              subscriptions: adminData.subscriptions || {},
              subscription_forms: adminData.subscription_forms || {},
              feedbacks: adminData.feedbacks || {},
              reviews: adminData.reviews || {}
            };
          }
          
          // # Setup admin notification listener
          this._setupAdminNotificationListener(uid);
        }
      }
      
      // # Setup user notification listener
      this._setupUserNotificationListener(uid);
      
      return {
        notifications: this._notifications,
        subsc_ending: this._subsc_ending,
        meetings: this._meetings,
        adminNotifications: this._adminNotifications
      };
    } catch (error) {
      console.error('Error initializing notifications:', error);
      return null;
    }
  }

  // # Helper to dispatch notification update event
  _dispatchNotificationUpdate() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('notification_update'));
    }
  }

  _setupUserNotificationListener(uid) {
    if (!this._notificationListeners.has(`notifications_${uid}`)) {
      const userUnsubscribe = onSnapshot(doc(db, 'user_notify', uid), (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          this._notifications = data.subscriptions || {};
          this._subsc_ending = data.subsc_ending || {};
          
          // # Handle meetings (both array and map formats for backward compatibility)
          if (data.meetings) {
            if (Array.isArray(data.meetings)) {
              // # Convert old array format to map format
              this._meetings = data.meetings.reduce((acc, meeting) => {
                if (meeting && meeting.id) {
                  acc[meeting.id] = meeting;
                }
                return acc;
              }, {});
            } else {
              // # Use map format directly
              this._meetings = data.meetings || {};
            }
          } else {
            this._meetings = {};
          }
          
          this._dispatchNotificationUpdate();
        }
      }, (error) => {
        console.error('Error in user notification listener:', error);
      });

      this._notificationListeners.set(`notifications_${uid}`, userUnsubscribe);
    }
  }

  _setupAdminNotificationListener(uid) {
    if (!this._notificationListeners.has(`admin_notifications_${uid}`)) {
      const adminUnsubscribe = onSnapshot(doc(db, 'adm_notify', uid), (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          this._adminNotifications = {
            subscriptions: data.subscriptions || {},
            subscription_forms: data.subscription_forms || {},
            feedbacks: data.feedbacks || {},
            reviews: data.reviews || {}
          };
          this._dispatchNotificationUpdate();
        }
      }, (error) => {
        console.error('Error in admin notification listener:', error);
      });

      this._notificationListeners.set(`admin_notifications_${uid}`, adminUnsubscribe);
    }
  }

  removeNotificationListeners(uid) {
    // # Remove user notification listener
    const userUnsubscribe = this._notificationListeners.get(`notifications_${uid}`);
    if (userUnsubscribe) {
      userUnsubscribe();
      this._notificationListeners.delete(`notifications_${uid}`);
    }

    // # Remove admin notification listener
    const adminUnsubscribe = this._notificationListeners.get(`admin_notifications_${uid}`);
    if (adminUnsubscribe) {
      adminUnsubscribe();
      this._notificationListeners.delete(`admin_notifications_${uid}`);
    }
  }

  // # Get current notifications state
  getNotifications() {
    // # Ensure we always return proper structure even if some fields are null
    return {
      notifications: this._notifications || {},
      subsc_ending: this._subsc_ending || {},
      meetings: this._meetings || {},
      adminNotifications: this._adminNotifications || {
        subscriptions: {},
        subscription_forms: {},
        feedbacks: {},
        reviews: {}
      }
    };
  }

  async markNotificationAsSeen(notificationId, uid) {
    try {
      const session = this.getNotifications();
      if (!session) return false;
      
      const userNotifyRef = doc(db, 'user_notify', uid);
      
      // # Check if this is an ending notification
      if (notificationId.startsWith('subsc_ending_')) {
        await updateDoc(userNotifyRef, {
          [`subsc_ending.${notificationId}.seen`]: true
        });
        // # Update local state
        if (this._subsc_ending && this._subsc_ending[notificationId]) {
          this._subsc_ending[notificationId].seen = true;
          this._dispatchNotificationUpdate();
        }
      } 
      // # Check if this is a meeting notification
      else if (notificationId.startsWith('meet_')) {
        await updateDoc(userNotifyRef, {
          [`meetings.${notificationId}.seen`]: true
        });
        // # Update local state
        if (this._meetings && this._meetings[notificationId]) {
          this._meetings[notificationId].seen = true;
          this._dispatchNotificationUpdate();
        }
      } 
      // # Regular notification
      else {
        await updateDoc(userNotifyRef, {
          [`subscriptions.${notificationId}.seen`]: true
        });
        // # Update local state
        if (this._notifications && this._notifications[notificationId]) {
          this._notifications[notificationId].seen = true;
          this._dispatchNotificationUpdate();
        }
      }
      return true;
    } catch (error) {
      console.error('Error marking notification as seen:', error);
      return false;
    }
  }

  async markAdminNotificationAsSeen(notificationId, uid, notificationType = 'subscription') {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      const token = await user.getIdTokenResult();
      if (!token.claims?.admin) return false;
      
      const adminNotifyRef = doc(db, 'adm_notify', uid);
      
      // # Determine which collection to update based on type
      const collectionKey = notificationType === 'subscription_form' 
        ? 'subscription_forms' 
        : notificationType === 'feedback'
        ? 'feedbacks'
        : notificationType === 'review'
        ? 'reviews'
        : 'subscriptions';
      
      await updateDoc(adminNotifyRef, {
        [`${collectionKey}.${notificationId}.seen`]: true
      });

      // # Update local state
      if (this._adminNotifications?.[collectionKey]?.[notificationId]) {
        this._adminNotifications[collectionKey][notificationId].seen = true;
        this._dispatchNotificationUpdate();
      }

      return true;
    } catch (error) {
      console.error('Error marking admin notification as seen:', error);
      return false;
    }
  }

  async markAllNotificationsAsSeen(uid) {
    try {
      const session = this.getNotifications();
      if (!session) return false;
      
      const updates = {};
      
      // # Mark regular notifications as seen
      if (this._notifications) {
        Object.keys(this._notifications).forEach(notificationId => {
          if (!this._notifications[notificationId].seen) {
            updates[`subscriptions.${notificationId}.seen`] = true;
          }
        });
      }
      
      // # Mark ending notifications as seen
      if (this._subsc_ending) {
        Object.keys(this._subsc_ending).forEach(notificationId => {
          if (!this._subsc_ending[notificationId].seen) {
            updates[`subsc_ending.${notificationId}.seen`] = true;
          }
        });
      }
      
      // # Mark meeting notifications as seen
      if (this._meetings && Object.keys(this._meetings).length > 0) {
        // # Update each meeting's seen status
        Object.keys(this._meetings).forEach(meetingId => {
          if (!this._meetings[meetingId].seen) {
            updates[`meetings.${meetingId}.seen`] = true;
            // # Update local state
            this._meetings[meetingId].seen = true;
          }
        });
      }
      
      if (Object.keys(updates).length > 0) {
        const userNotifyRef = doc(db, 'user_notify', uid);
        await updateDoc(userNotifyRef, updates);
      }
      return true;
    } catch (error) {
      console.error('Error marking all notifications as seen:', error);
      return false;
    }
  }

  async markAllAdminNotificationsAsSeen(uid) {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      const token = await user.getIdTokenResult();
      if (!token.claims?.admin) return false;

      const adminNotifyRef = doc(db, 'adm_notify', uid);
      const adminNotifyDoc = await getDoc(adminNotifyRef);
      
      if (!adminNotifyDoc.exists()) return false;
      
      const data = adminNotifyDoc.data();
      const updates = {};
      
      // # Mark subscription form notifications as seen
      if (data.subscription_forms) {
        Object.keys(data.subscription_forms).forEach(id => {
          updates[`subscription_forms.${id}.seen`] = true;
          if (this._adminNotifications?.subscription_forms?.[id]) {
            this._adminNotifications.subscription_forms[id].seen = true;
          }
        });
      }
      
      // # Mark subscription notifications as seen
      if (data.subscriptions) {
        Object.keys(data.subscriptions).forEach(id => {
          updates[`subscriptions.${id}.seen`] = true;
          if (this._adminNotifications?.subscriptions?.[id]) {
            this._adminNotifications.subscriptions[id].seen = true;
          }
        });
      }
      
      if (Object.keys(updates).length > 0) {
        await updateDoc(adminNotifyRef, updates);
        this._dispatchNotificationUpdate();
      }
      
      return true;
    } catch (error) {
      console.error('Error marking all admin notifications as seen:', error);
      return false;
    }
  }

  async deleteNotification(notificationId, uid) {
    try {
      const session = this.getNotifications();
      if (!session) return false;
      
      const userNotifyRef = doc(db, 'user_notify', uid);
      
      // # Determine the notification type
      const isEndingNotification = notificationId.startsWith('subsc_ending_');
      const isMeetingNotification = notificationId.startsWith('meet_');
      
      if (isMeetingNotification) {
        // # For meetings map, use deleteField for the specific meeting
        await updateDoc(userNotifyRef, {
          [`meetings.${notificationId}`]: deleteField()
        });
        
        // # Update local state
        if (this._meetings) {
          delete this._meetings[notificationId];
        }
      } else {
        // # For regular or ending notifications
        const collectionKey = isEndingNotification ? 'subsc_ending' : 'subscriptions';
        
        // # Delete the notification using deleteField()
        await updateDoc(userNotifyRef, {
          [`${collectionKey}.${notificationId}`]: deleteField()
        });

        // # Update local state
        if (isEndingNotification && this._subsc_ending) {
          delete this._subsc_ending[notificationId];
        } else if (this._notifications) {
          delete this._notifications[notificationId];
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  }

  async deleteAdminNotification(notificationId, uid, notificationType = 'subscription') {
    try {
      const session = this.getNotifications();
      if (!session) return false;
      
      const userNotifyRef = doc(db, 'adm_notify', uid);
      
      // # Determine which collection to update based on type
      const collectionKey = notificationType === 'subscription_form' 
        ? 'subscription_forms' 
        : notificationType === 'feedback'
        ? 'feedbacks'
        : notificationType === 'review'
        ? 'reviews'
        : 'subscriptions';
      
      // # Delete the notification using deleteField()
      await updateDoc(userNotifyRef, {
        [`${collectionKey}.${notificationId}`]: deleteField()
      });

      // # Update local state
      if (this._adminNotifications && this._adminNotifications[collectionKey]) {
        delete this._adminNotifications[collectionKey][notificationId];
        this._dispatchNotificationUpdate();
      }

      return true;
    } catch (error) {
      console.error('Error deleting admin notification:', error);
      return false;
    }
  }

  async clearAllNotifications(uid) {
    try {
      const session = this.getNotifications();
      if (!session) return false;
      
      const userNotifyRef = doc(db, 'user_notify', uid);
      
      // # Clear all notifications
      await updateDoc(userNotifyRef, {
        subscriptions: {},
        subsc_ending: {},
        meetings: {}  // # Changed from [] to {} for map structure
      });

      // # Update local state
      this._notifications = {};
      this._subsc_ending = {};
      this._meetings = {};  // # Changed from [] to {} for map structure
      
      this._dispatchNotificationUpdate();
      return true;
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      return false;
    }
  }

  async clearAllAdminNotifications(uid) {
    try {
      const session = this.getNotifications();
      if (!session) return false;
      
      const userNotifyRef = doc(db, 'adm_notify', uid);
      
      // # Clear both subscription and subscription form notifications
      await updateDoc(userNotifyRef, {
        subscriptions: {},
        subscription_forms: {}
      });

      // # Update local state
      if (this._adminNotifications) {
        this._adminNotifications = {
          subscriptions: {},
          subscription_forms: {}
        };
      }

      return true;
    } catch (error) {
      console.error('Error clearing all admin notifications:', error);
      return false;
    }
  }

  // # Clean up old seen notifications
  async cleanupOldNotifications(uid) {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      
      // # Handle admin notifications if user is admin
      const token = await user.getIdTokenResult();
      if (token.claims?.admin) {
        const adminNotifyRef = doc(db, 'adm_notify', uid);
        const adminDoc = await getDoc(adminNotifyRef);
        
        if (adminDoc.exists()) {
          const data = adminDoc.data();
          const updates = {};
          let hasUpdates = false;

          // # Check subscription form notifications
          if (data.subscription_forms) {
            Object.entries(data.subscription_forms).forEach(([notifId, notif]) => {
              if (notif.seen && new Date(notif.metadata.createdAt) < sevenDaysAgo) {
                updates[`subscription_forms.${notifId}`] = deleteField();
                hasUpdates = true;
              }
            });
          }

          // # Check subscription notifications
          if (data.subscriptions) {
            Object.entries(data.subscriptions).forEach(([notifId, notif]) => {
              if (notif.seen && new Date(notif.metadata.createdAt) < sevenDaysAgo) {
                updates[`subscriptions.${notifId}`] = deleteField();
                hasUpdates = true;
              }
            });
          }

          if (hasUpdates) {
            await updateDoc(adminNotifyRef, updates);
            
            // # Update local state
            Object.keys(updates).forEach(path => {
              const [collection, id] = path.split('.');
              if (this._adminNotifications?.[collection]?.[id]) {
                delete this._adminNotifications[collection][id];
              }
            });
            
            this._dispatchNotificationUpdate();
          }
        }
      }

      // # Handle user notifications
      const userNotifyRef = doc(db, 'user_notify', uid);
      const userDoc = await getDoc(userNotifyRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const updates = {};
        let hasUpdates = false;

        // # Check regular notifications
        if (data.subscriptions) {
          Object.entries(data.subscriptions).forEach(([notifId, notif]) => {
            if (notif.seen && new Date(notif.metadata.createdAt) < sevenDaysAgo) {
              updates[`subscriptions.${notifId}`] = deleteField();
              hasUpdates = true;
            }
          });
        }

        // # Check ending notifications
        if (data.subsc_ending) {
          Object.entries(data.subsc_ending).forEach(([notifId, notif]) => {
            if (notif.seen && new Date(notif.metadata.createdAt) < sevenDaysAgo) {
              updates[`subsc_ending.${notifId}`] = deleteField();
              hasUpdates = true;
            }
          });
        }
        
        // # Check meeting notifications - updated for map structure
        if (data.meetings && !Array.isArray(data.meetings)) {
          // # For map structure
          Object.entries(data.meetings).forEach(([meetingId, meeting]) => {
            // # Clean up meetings that are:
            // # 1. Seen AND
            // # 2. Created more than 7 days ago
            const createdAt = meeting.created_at || meeting.metadata?.createdAt;
            if (meeting.seen && createdAt && new Date(createdAt) < sevenDaysAgo) {
              updates[`meetings.${meetingId}`] = deleteField();
              // # Update local state
              if (this._meetings && this._meetings[meetingId]) {
                delete this._meetings[meetingId];
              }
              hasUpdates = true;
            }
          });
        } else if (data.meetings && Array.isArray(data.meetings)) {
          // # For backward compatibility - array structure
          const currentMeetings = data.meetings.filter(meeting => {
            // # Keep meetings that are:
            // # 1. Not seen, or
            // # 2. Created within the last 7 days
            const createdAt = meeting.created_at || meeting.metadata?.createdAt;
            return !meeting.seen || (createdAt && new Date(createdAt) >= sevenDaysAgo);
          });
          
          if (currentMeetings.length < data.meetings.length) {
            // # Convert to map structure when updating
            const meetingsMap = currentMeetings.reduce((acc, meeting) => {
              if (meeting && meeting.id) {
                acc[meeting.id] = meeting;
              }
              return acc;
            }, {});
            
            updates.meetings = meetingsMap;
            this._meetings = meetingsMap;
            hasUpdates = true;
          }
        }

        if (hasUpdates) {
          await updateDoc(userNotifyRef, updates);
          
          // # Update local state
          Object.keys(updates).forEach(path => {
            if (path.startsWith('meetings.')) {
              // # Already updated above
            } else if (path !== 'meetings') {
              const [collection, id] = path.split('.');
              if (collection === 'subscriptions' && this._notifications) {
                delete this._notifications[id];
              } else if (collection === 'subsc_ending' && this._subsc_ending) {
                delete this._subsc_ending[id];
              }
            }
          });
          
          this._dispatchNotificationUpdate();
        }
      }

      return true;
    } catch (error) {
      console.error('Error cleaning up notifications:', error);
      return false;
    }
  }

  // # Cleanup on destroy
  destroy() {
    this._notificationListeners.forEach((unsubscribe) => unsubscribe());
    this._notificationListeners.clear();
    this._notifications = null;
    this._adminNotifications = {
      subscriptions: {},
      subscription_forms: {},
      feedbacks: {},
      reviews: {}
    };
    this._subsc_ending = null;
    this._meetings = null;
  }
}

// # Create singleton instance
const notificationManager = new NotificationSessionManager();

// # Export singleton
export default notificationManager;
